import { formatMoney } from '@/lib/format';
import type { NumberRange, SellField, SellFieldProblem } from './sellFormState';

/** Раздел формы предложения: заголовок раздела и строка «Осталось: …» до первой попытки отправки. */
export type SellSection = 'where' | 'goods' | 'price' | 'logistics' | 'declaration' | 'special' | 'agent' | 'agree';

/** Порядок разделов на экране: по нему сортируется сводка того, что мешает отправке. */
export const SECTION_ORDER: readonly SellSection[] = ['where', 'goods', 'price', 'logistics', 'declaration', 'special', 'agent', 'agree'];

export const SECTION_TITLE: Record<SellSection, string> = {
  where: 'app.sell.form.sectionWhere',
  goods: 'app.sell.form.sectionGoods',
  price: 'app.sell.form.sectionPrice',
  logistics: 'app.sell.form.sectionLogistics',
  declaration: 'app.sell.form.sectionDeclaration',
  special: 'app.sell.form.sectionSpecial',
  agent: 'app.sell.form.sectionAgent',
  agree: 'app.sell.form.sectionAgree',
};

/** Подпись поля в сводке ошибок — та же, что у самого поля на экране. */
export const FIELD_LABEL: Record<SellField, string> = {
  address: 'app.sell.form.address',
  goods: 'app.sell.form.goods',
  quality: 'app.sell.form.quality',
  weight: 'app.sell.form.weight',
  price: 'app.sell.form.price',
  prepayment: 'app.sell.form.prepayment',
  credit: 'app.sell.form.credit',
  startDate: 'app.sell.form.loading.startDate',
  endDate: 'app.sell.form.loading.endDate',
  shipmentModes: 'app.sell.form.loading.modes',
  rate: 'app.sell.form.loading.rateValue',
  vehicleHeight: 'app.sell.form.loading.vehicleHeight',
  scalesLength: 'app.sell.form.loading.scalesLength',
  storagePrice: 'app.sell.form.loading.storagePrice',
  declarationNo: 'app.sell.form.declaration.no',
  declarationDate: 'app.sell.form.declaration.date',
  declarationFiles: 'app.sell.form.declaration.files',
  specialFiles: 'app.sell.form.special.files',
};

export const FIELD_SECTION: Record<SellField, SellSection> = {
  address: 'where',
  goods: 'goods',
  quality: 'goods',
  weight: 'goods',
  price: 'price',
  prepayment: 'price',
  credit: 'price',
  startDate: 'logistics',
  endDate: 'logistics',
  shipmentModes: 'logistics',
  rate: 'logistics',
  vehicleHeight: 'logistics',
  scalesLength: 'logistics',
  storagePrice: 'logistics',
  declarationNo: 'declaration',
  declarationDate: 'declaration',
  declarationFiles: 'declaration',
  specialFiles: 'special',
};

/** Идентификатор элемента поля на странице: по нему сводка ведёт к полю и переводит на него фокус. */
export function sellFieldId(field: SellField | SellSection): string {
  return `sell-field-${field}`;
}

/** Перевести фокус на поле (или на раздел, если поле само фокус не принимает) и показать его. */
export function focusSellField(target: SellField | SellSection): void {
  const element = document.getElementById(sellFieldId(target));
  if (!element) {
    return;
  }
  // Заблокированное поле фокус не принимает: тогда только показываем его.
  const focusable = element.matches('input, textarea, button, select, [tabindex]')
    ? element
    : element.querySelector<HTMLElement>('input:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex="-1"]');
  if (focusable && !focusable.matches(':disabled')) {
    focusable.focus({ preventScroll: true });
  }
  element.scrollIntoView({ block: 'center', behavior: 'auto' });
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * Текст ошибки поля. Значение вне диапазона отмечается сразу: форма его не подменяет, поэтому продавец должен видеть
 * ошибку, пока не исправит. Пустое обязательное поле отмечается только после попытки отправки, чтобы нетронутая форма
 * не была красной. Ошибки дат и биржевого лота форма показывает своими текстами.
 */
export function fieldError(
  problems: ReadonlyMap<SellField, SellFieldProblem>,
  field: SellField,
  attempted: boolean,
  t: Translate,
  range?: NumberRange,
): string | undefined {
  const problem = problems.get(field);
  if (!problem || problem.reason === 'date') {
    return undefined;
  }
  if (problem.reason === 'range') {
    return range ? t('app.sell.form.rangeErr', { min: formatMoney(range.min), max: formatMoney(range.max) }) : undefined;
  }
  return attempted ? t('auth.validation.required') : undefined;
}
